import React, { useMemo, useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import {
	MoreVertical,
	FileText,
	Tag,
	Archive,
	Loader,
	Edit,
	UserCheck,
	Cpu,
	FilePlus
} from 'react-feather';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import { useSearchParams } from 'react-router-dom';

import { useSupabaseStore } from './supabaseStore';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';
import { useChatStore, subscribeToMessages, SendMessageOptions } from './useChat';
import { CustomerSupportStatus } from './types';
import ArchiveModal from './ChatMenuOptions/ArchiveModal';
import UpdateTagModal from './ChatMenuOptions/UpdateTagModal';
import TemplateMessageModal from './ChatMenuOptions/TemplateMessageModal';
import CreateTemplateModal from './ChatMenuOptions/CreateTemplateModal';
import UpdateCustomerNameModal from './ChatMenuOptions/UpdateCustomerNameModal';
import PreapproveModal from './ChatMenuOptions/PreApproveModal';
import Button from '@/UIlibrary/Button/Button';

const ChatWindow: React.FC = () => {
	const t = useTranslations('Support');
	const supabase = useSupabaseStore((state) => state.supabase);
	const { selectedChat, messages, sendMessage, searchTerm, isLoadingMessages } = useChatStore();

	const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
	const [isCreateTemplateModalOpen, setIsCreateTemplateModalOpen] = useState(false);
	const [isNewChatTemplateModalOpen, setIsNewChatTemplateModalOpen] = useState(false);
	const [isUpdateTagModalOpen, setIsUpdateTagModalOpen] = useState(false);
	const [isUpdateCustomerNameModalOpen, setIsUpdateCustomerNameModalOpen] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
	const [isPreapproveModalOpen, setIsPreapproveModalOpen] = useState(false);

	const [isEnablingAIResponses, setIsEnablingAIResponses] = useState(false);
	const [isDisablingAIResponses, setIsDisablingAIResponses] = useState(false);

	const [searchParams] = useSearchParams();
	const fullNameFromParams = searchParams.get('name');
	// Subscribe to messages when selectedChat changes
	useEffect(() => {
		let messageChannel: ReturnType<typeof subscribeToMessages> | null = null;

		if (selectedChat?.id) {
			console.log('Setting up message subscription for chat:', selectedChat.id);
			messageChannel = subscribeToMessages(selectedChat.id);
		}

		return () => {
			if (messageChannel) {
				console.log('Cleaning up message subscription');
				messageChannel.unsubscribe();
			}
		};
	}, [selectedChat]);

	const formattedPhone = useMemo(() => {
		if (!selectedChat?.customer_phone) return '';

		const formatPhone = (phone: string) => {
			const cleaned = phone?.replace(/\D/g, '');

			if (cleaned?.startsWith('521')) {
				return cleaned.slice(3);
			} else if (cleaned?.startsWith('52')) {
				return cleaned.slice(2);
			} else {
				return cleaned?.slice(-10);
			}
		};

		return formatPhone(selectedChat.customer_phone);
	}, [selectedChat?.customer_phone]);

	// Check if last client message is within 24 hours
	const hasRecentClientMessage = useMemo(() => {
		if (!messages.length) return false;

		const clientMessages = messages.filter((msg) => msg.sender_type === 'client');
		if (!clientMessages.length) return false;

		const lastClientMessage = clientMessages[clientMessages.length - 1];
		const lastClientInteractionTime = new Date(lastClientMessage.sent_at).getTime();
		const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

		return lastClientInteractionTime >= twentyFourHoursAgo;
	}, [messages]);

	// Handle sending a message
	const handleSendMessage = async (message: string, options?: SendMessageOptions) => {
		if (!selectedChat?.id) return false;
		const success = await sendMessage(selectedChat.id, message, options);

		console.log(
			`Message send attempt for chat ${selectedChat.id}:`,
			success ? 'success' : 'failed'
		);

		return success;
	};

	const openArchiveModal = () => {
		setIsArchiveModalOpen(true);
		setIsMenuOpen(false);
	};

	const openPreapproveModal = () => {
		setIsPreapproveModalOpen(true);
		setIsMenuOpen(false);
	};

	const enableAIResponses = async () => {
		if (!selectedChat?.id || !supabase) return;

		try {
			setIsEnablingAIResponses(true);
			const event = 'CUSTOMER_SUPPORT_REPRESENTATIVE_ENABLES_AI';

			const { error } = await supabase.functions.invoke('chat-customer-support-status-transition', {
				body: {
					chatId: selectedChat.id,
					event: event,
					metadata: {}
				}
			});

			if (error) {
				console.error('Error invoking transition-state function:', error);
				return;
			}
		} catch (error) {
			console.error('Failed to enable AI responses:', error);
		} finally {
			setIsEnablingAIResponses(false);
			setIsMenuOpen(false);
		}
	};

	const disableAIResponses = async () => {
		if (!selectedChat?.id || !supabase) return;

		try {
			setIsDisablingAIResponses(true);
			const event = 'CUSTOMER_SUPPORT_DISABLES_AI';

			const { error } = await supabase.functions.invoke('chat-customer-support-status-transition', {
				body: {
					chatId: selectedChat.id,
					event: event,
					metadata: {}
				}
			});

			if (error) {
				console.error('Error invoking transition-state function:', error);
				return;
			}
		} catch (error) {
			console.error('Failed to disable AI responses:', error);
		} finally {
			setIsDisablingAIResponses(false);
			setIsMenuOpen(false);
		}
	};

	if (!selectedChat) {
		return (
			<div className="flex-1 flex items-center justify-center">
				{searchTerm && searchTerm.trim() ? (
					<div className="flex flex-col items-center space-y-4">
						<Typography color="subtitle">
							{t('noChatFound')} {searchTerm.trim()}
						</Typography>
						<Button
							onClick={() => {
								setIsNewChatTemplateModalOpen(!isNewChatTemplateModalOpen);
							}}
							label={t('createNewChat')}
							color="primary"
							size="auto"
						/>
					</div>
				) : (
					<Typography color="subtitle">{t('selectChat')}</Typography>
				)}
				<TemplateMessageModal
					defaultPhone={searchTerm.trim()}
					defaultName={fullNameFromParams ?? undefined}
					isOpen={isNewChatTemplateModalOpen}
					onClose={() => setIsNewChatTemplateModalOpen(false)}
					defaultTemplateSearch={'aprobado_para_comite_credito'}
					defaultTag={'originacion_microcredito'}
					isNewChat={true}
				/>
			</div>
		);
	}

	const shouldSendTemplateMessageBeDisabled = () => {
		if (!selectedChat?.customer_phone) return true;
		const sendingTemplateIsValidStatuses = [
			CustomerSupportStatus.ANSWER_SENT,
			CustomerSupportStatus.CLIENT_HAS_NOT_ANSWERED,
			CustomerSupportStatus.AI_RESPONSES,
			CustomerSupportStatus.NEEDS_REVIEW,
			CustomerSupportStatus.PREAPPROVED
		];
		const isChatInValidStatus = !sendingTemplateIsValidStatuses.includes(
			selectedChat.customer_support_status
		);

		return isChatInValidStatus;
	};

	const shouldEnablingAIResponsesBeDisabled = () => {
		if (!selectedChat?.customer_support_status) return true;
		if (isEnablingAIResponses) return true;
		if (isDisablingAIResponses) return true;

		const enablingAIResponsesValidStatuses = [
			CustomerSupportStatus.NEEDS_REVIEW,
			CustomerSupportStatus.AI_RESPONSES,
			CustomerSupportStatus.ANSWER_SENT,
			CustomerSupportStatus.PREAPPROVED,
			CustomerSupportStatus.CLIENT_HAS_NOT_ANSWERED,
			CustomerSupportStatus.ARCHIVED
		];

		const isChatInValidState = enablingAIResponsesValidStatuses.includes(
			selectedChat.customer_support_status
		);

		return !isChatInValidState;
	};

	const getAIResponsesButtonText = () => {
		if (isEnablingAIResponses) return t('EnablingAIResponses');
		if (isDisablingAIResponses) return t('DisablingAIResponses');
		if (selectedChat?.customer_support_status === CustomerSupportStatus.AI_RESPONSES) {
			return t('DisableAIResponses');
		}
		return t('EnableAIResponses');
	};

	const handleAiResponsesButtonClick = () => {
		if (selectedChat?.customer_support_status === CustomerSupportStatus.AI_RESPONSES) {
			disableAIResponses();
		} else {
			enableAIResponses();
		}
	};

	const shouldArchivingBeDisabled = () => {
		if (!selectedChat?.customer_support_status) return true;

		const archivingValidStates = [
			CustomerSupportStatus.CLIENT_HAS_NOT_ANSWERED,
			CustomerSupportStatus.NEEDS_REVIEW
		];

		const isChatInValidState = archivingValidStates.includes(selectedChat.customer_support_status);

		return !isChatInValidState;
	};

	const CustomerSupportStatusBadge = () => {
		switch (selectedChat.customer_support_status) {
			case CustomerSupportStatus.AI_RESPONSES:
				return (
					<TextBadge text={t('customerSupportStatus.AI_RESPONSES')} type="primary" size="sm" />
				);

			case CustomerSupportStatus.NEEDS_REVIEW:
				return (
					<TextBadge text={t('customerSupportStatus.NEEDS_REVIEW')} type="warning" size="sm" />
				);
			case CustomerSupportStatus.CLIENT_HAS_NOT_ANSWERED:
				return (
					<TextBadge
						text={t('customerSupportStatus.CLIENT_HAS_NOT_ANSWERED')}
						type="error"
						size="sm"
					/>
				);
			case CustomerSupportStatus.ARCHIVED:
				return <TextBadge text={t('customerSupportStatus.ARCHIVED')} type="gray" size="sm" />;
			case CustomerSupportStatus.ANSWER_SENT:
				return <TextBadge text={t('customerSupportStatus.ANSWER_SENT')} type="success" size="sm" />;
			case CustomerSupportStatus.PREAPPROVED:
				return <TextBadge text={t('customerSupportStatus.PREAPPROVED')} type="success" size="sm" />;
			default:
				return null;
		}
	};

	return (
		<div className="flex-1 flex flex-col ">
			<div className="flex justify-between items-center mb-1 p-4 pb-1 px-12 max-h-[calc(15vh)]">
				<div className="flex items-center space-x-3">
					<Typography color="subtitle" size="lg">
						{t('Chat')} #{selectedChat.id}
					</Typography>

					{<CustomerSupportStatusBadge />}
				</div>

				<div className="relative">
					<button
						onClick={() => setIsMenuOpen(!isMenuOpen)}
						className="p-2 hover:bg-gray-100 rounded-full transition-colors"
					>
						<MoreVertical size={20} />
					</button>

					{isMenuOpen && (
						<div className="absolute right-0 mt-2 w-[350px] bg-white rounded-md shadow-lg z-50">
							<div className="py-1">
								<div className="relative group">
									<button
										disabled={shouldSendTemplateMessageBeDisabled()}
										onClick={() => {
											setIsTemplateModalOpen(true);
											setIsMenuOpen(false);
										}}
										className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
									>
										<FileText size={16} className="mr-2" />
										{t('sendTemplateMessage')}
									</button>
									{shouldSendTemplateMessageBeDisabled() && (
										<div className="invisible group-hover:visible absolute -top-12 left-0 w-full bg-gray-600 text-white p-2 rounded text-xs z-50">
											{t('TemplateMessageDisabledReason')}
										</div>
									)}
								</div>

								<div className="relative group">
									<button
										disabled={shouldSendTemplateMessageBeDisabled()}
										onClick={() => {
											setIsCreateTemplateModalOpen(true);
											setIsMenuOpen(false);
										}}
										className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
									>
										<FilePlus size={16} className="mr-2" />
										{t('CreateTemplateMessage')}
									</button>
									{shouldSendTemplateMessageBeDisabled() && (
										<div className="invisible group-hover:visible absolute -top-12 left-0 w-full bg-gray-600 text-white p-2 rounded text-xs z-50">
											{t('TemplateMessageDisabledReason')}
										</div>
									)}
								</div>

								<button
									onClick={() => {
										setIsUpdateTagModalOpen(true);
										setIsMenuOpen(false);
									}}
									className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
								>
									<Tag size={16} className="mr-2" />
									{t('updateTag')}
								</button>

								<button
									onClick={() => {
										setIsUpdateCustomerNameModalOpen(true);
										setIsMenuOpen(false);
									}}
									className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
								>
									<Edit size={16} className="mr-2" />
									{t('UpdateCustomerName')}
								</button>

								<div className="relative group">
									<button
										onClick={handleAiResponsesButtonClick}
										className={`flex items-center px-4 py-2 text-sm w-full hover:bg-gray-100 ${
											shouldEnablingAIResponsesBeDisabled()
												? 'text-gray-400 cursor-not-allowed'
												: 'text-gray-700 '
										}`}
										disabled={shouldEnablingAIResponsesBeDisabled()}
									>
										{isEnablingAIResponses ? (
											<Loader size={16} className="mr-2 animate-spin" />
										) : (
											<Cpu size={16} className="mr-2" />
										)}
										{getAIResponsesButtonText()}
									</button>
									{shouldEnablingAIResponsesBeDisabled() && !isEnablingAIResponses && (
										<div className="invisible group-hover:visible absolute -top-12 left-0 w-full bg-gray-600 text-white p-2 rounded text-xs z-50">
											{t('AIDisabledReason')}
										</div>
									)}
								</div>

								<div className="relative group">
									<button
										onClick={openPreapproveModal}
										className={`flex items-center px-4 py-2 text-sm w-full text-gray-700 hover:bg-gray-100`}
									>
										<UserCheck size={16} className="mr-2" />
										{t('PreapproveClient')}
									</button>
								</div>

								<div className="relative group">
									<button
										onClick={openArchiveModal}
										className={`flex items-center px-4 py-2 text-sm w-full ${
											shouldArchivingBeDisabled()
												? 'text-gray-400 cursor-not-allowed'
												: 'text-gray-700 hover:bg-gray-100'
										}`}
										disabled={shouldArchivingBeDisabled()}
									>
										<Archive size={16} className="mr-2" />
										{t('ArchiveChat')}
									</button>
									{shouldArchivingBeDisabled() && (
										<div className="invisible group-hover:visible absolute -top-12 left-0 w-full bg-gray-600 text-white p-2 rounded text-xs z-50">
											{t('ArchiveDisabledReason')}
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			{!hasRecentClientMessage && (
				<div
					className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4"
					role="alert"
				>
					<Typography color="warning" size="sm">
						{t('DisclaimerInactiveChat')}
					</Typography>
				</div>
			)}
			<div className="overflow-auto max-h-[calc(65vh)] lg:max-h-[calc(75vh)]">
				<MessageList messages={messages} isLoadingMessages={isLoadingMessages} />
			</div>

			{hasRecentClientMessage && (
				<MessageInput
					onSendMessage={handleSendMessage}
					disabled={!selectedChat?.id}
					chatId={selectedChat.id}
				/>
			)}

			<TemplateMessageModal
				defaultPhone={formattedPhone}
				isOpen={isTemplateModalOpen}
				onClose={() => setIsTemplateModalOpen(false)}
				isNewChat={false}
			/>

			<CreateTemplateModal
				isOpen={isCreateTemplateModalOpen}
				onClose={() => setIsCreateTemplateModalOpen(false)}
			/>

			<UpdateTagModal
				isOpen={isUpdateTagModalOpen}
				onClose={() => setIsUpdateTagModalOpen(false)}
			/>

			<UpdateCustomerNameModal
				isOpen={isUpdateCustomerNameModalOpen}
				onClose={() => setIsUpdateCustomerNameModalOpen(false)}
			/>
			<ArchiveModal isOpen={isArchiveModalOpen} onClose={() => setIsArchiveModalOpen(false)} />
			<PreapproveModal
				isOpen={isPreapproveModalOpen}
				onClose={() => setIsPreapproveModalOpen(false)}
			/>
		</div>
	);
};

export default ChatWindow;
